import { useLayoutEffect, useRef, type RefObject } from "react";

export type TeeSheetNavAutoScrollRow = {
  start_time: string;
  slots?: Array<{ tee_time_id?: number }>;
};

export type TeeSheetNavAutoScrollDecision =
  | { type: "scroll"; teeTimeId: number; block?: ScrollLogicalPosition }
  | { type: "clear" };

export type TeeSheetNavAutoScrollDecide<T extends TeeSheetNavAutoScrollRow> = (
  sorted: T[],
) => TeeSheetNavAutoScrollDecision;

/** Runs one smooth auto-scroll per navigation/row-count signature. */
export function useTeeSheetNavigationAutoScroll<T extends TeeSheetNavAutoScrollRow>(
  navigationKey: string,
  sortedTeeTimes: T[] | undefined,
  decide: TeeSheetNavAutoScrollDecide<T>,
  scrollRootRef?: RefObject<HTMLElement | null>,
): void {
  const sortedRef = useRef(sortedTeeTimes);
  sortedRef.current = sortedTeeTimes;
  const decideRef = useRef(decide);
  decideRef.current = decide;
  const lastSignatureRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const len = sortedTeeTimes?.length ?? 0;
    const signature = `${navigationKey}|${len}`;
    if (len === 0 || lastSignatureRef.current === signature) {
      return;
    }

    // Use refs so the effect can stay keyed to navigation/length only.
    const sorted = sortedRef.current ?? [];
    const decision = decideRef.current(sorted);
    if (decision.type === "clear") {
      lastSignatureRef.current = signature;
      return;
    }

    const idStr = String(decision.teeTimeId);
    const root = scrollRootRef?.current;
    const el = root ? root.querySelector<HTMLElement>(`#${CSS.escape(idStr)}`) : document.getElementById(idStr);

    el?.scrollIntoView({ behavior: "smooth", block: decision.block ?? "center", inline: "nearest" });

    lastSignatureRef.current = signature;
  }, [navigationKey, sortedTeeTimes?.length ?? 0]);
}
